import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';
import DateFilter, { filterByDateRange } from 'components/ui/DateFilter';
import HeadToHead from './HeadToHead';

const SORT_COLUMNS = [
  { key: 'name', label: 'Player' },
  { key: 'wins', label: 'W' },
  { key: 'losses', label: 'L' },
  { key: 'winPct', label: 'Win%' },
  { key: 'avgPoints', label: 'PTS' },
  { key: 'avgRebounds', label: 'REB' },
  { key: 'avgAssists', label: 'AST' },
];

// Compute stats for all players using a filtered set of games and stats.
const computePlayerRowsFor = (players, games, gameStats) => {
  const gameMap = {};
  games?.forEach((g) => { gameMap[g.id] = g; });
  const gameIds = new Set(games?.map((g) => g.id) || []);
  const filteredStats = gameStats?.filter((s) => gameIds.has(s?.game_id)) || [];

  return players?.map((p) => {
    const pStats = filteredStats.filter((s) => s?.player_id === p?.id);
    const total = pStats.length;
    const wins = pStats.filter((s) => {
      const g = gameMap[s?.game_id];
      return g && g.winning_team === s?.team;
    }).length;
    return {
      id: p?.id,
      name: p?.name,
      position: p?.position,
      gamesPlayed: total,
      wins,
      losses: total - wins,
      winPct: total > 0 ? Math.round((wins / total) * 100) : 0,
      avgPoints: total > 0 ? parseFloat((pStats.reduce((s, r) => s + (r?.points || 0), 0) / total).toFixed(1)) : 0,
      avgRebounds: total > 0 ? parseFloat((pStats.reduce((s, r) => s + (r?.rebounds || 0), 0) / total).toFixed(1)) : 0,
      avgAssists: total > 0 ? parseFloat((pStats.reduce((s, r) => s + (r?.assists || 0), 0) / total).toFixed(1)) : 0,
      avgSteals: total > 0 ? parseFloat((pStats.reduce((s, r) => s + (r?.steals || 0), 0) / total).toFixed(1)) : 0,
      avgBlocks: total > 0 ? parseFloat((pStats.reduce((s, r) => s + (r?.blocks || 0), 0) / total).toFixed(1)) : 0,
    };
  }) || [];
};

// Compute combo (duos/trios) stats using a filtered set.
const computeComboStatsFor = (players, games, gameStats) => {
  const gameMap = {};
  games?.forEach((g) => { gameMap[g.id] = g; });
  const gameIds = new Set(games?.map((g) => g.id) || []);
  const filteredStats = gameStats?.filter((s) => gameIds.has(s?.game_id)) || [];

  const teamGroups = {};
  filteredStats.forEach((stat) => {
    const key = `${stat?.game_id}__${stat?.team}`;
    if (!teamGroups[key]) teamGroups[key] = { gameId: stat?.game_id, team: stat?.team, playerIds: [] };
    teamGroups[key].playerIds.push(stat?.player_id);
  });

  const comboCounts = {};
  Object.values(teamGroups).forEach((group) => {
    const { gameId, team, playerIds } = group;
    const game = gameMap[gameId];
    if (!game) return;
    const isWin = game.winning_team === team;

    for (let i = 0; i < playerIds.length; i++) {
      for (let j = i + 1; j < playerIds.length; j++) {
        const key = [playerIds[i], playerIds[j]].sort().join('-');
        if (!comboCounts[key]) comboCounts[key] = { playerIds: [playerIds[i], playerIds[j]].sort(), games: 0, wins: 0, size: 2 };
        comboCounts[key].games++;
        if (isWin) comboCounts[key].wins++;
      }
    }

    for (let i = 0; i < playerIds.length; i++) {
      for (let j = i + 1; j < playerIds.length; j++) {
        for (let k = j + 1; k < playerIds.length; k++) {
          const key = [playerIds[i], playerIds[j], playerIds[k]].sort().join('-');
          if (!comboCounts[key]) comboCounts[key] = { playerIds: [playerIds[i], playerIds[j], playerIds[k]].sort(), games: 0, wins: 0, size: 3 };
          comboCounts[key].games++;
          if (isWin) comboCounts[key].wins++;
        }
      }
    }
  });

  const playerMap = {};
  players?.forEach((p) => { playerMap[p?.id] = p?.name; });

  const allCombos = Object.values(comboCounts)
    .filter((c) => c.games >= 2)
    .map((c) => ({
      ...c,
      playerNames: c.playerIds.map((id) => playerMap[id] || 'Unknown'),
      winPct: Math.round((c.wins / c.games) * 100),
    }))
    .sort((a, b) => b.winPct - a.winPct || b.games - a.games);

  return {
    duos: allCombos.filter((c) => c.size === 2),
    trios: allCombos.filter((c) => c.size === 3),
  };
};

const StatsPage = () => {
  const { players, games, gameStats, gamesLoading } = useApp();
  const [sortKey, setSortKey] = useState('winPct');
  const [sortDir, setSortDir] = useState('desc');
  const [comboTab, setComboTab] = useState('duos');
  const [dateRange, setDateRange] = useState('all');
  const [mainTab, setMainTab] = useState('standings');

  const filteredGames = useMemo(() => filterByDateRange(games, dateRange), [games, dateRange]);

  const playerRows = useMemo(() => {
    const rows = computePlayerRowsFor(players, filteredGames, gameStats);
    return rows.sort((a, b) => {
      if (sortKey === 'name') {
        return sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }
      return sortDir === 'asc' ? (a[sortKey] ?? 0) - (b[sortKey] ?? 0) : (b[sortKey] ?? 0) - (a[sortKey] ?? 0);
    });
  }, [players, filteredGames, gameStats, sortKey, sortDir]);

  const comboStats = useMemo(() => computeComboStatsFor(players, filteredGames, gameStats), [players, filteredGames, gameStats]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const combos = comboTab === 'duos' ? comboStats?.duos : comboStats?.trios;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-4">
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>Stats & Standings</h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Player records, averages, and team chemistry</p>
          </div>

          {/* Main tab switcher + date filter */}
          <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
              {[
                { key: 'standings', label: 'Standings' },
                { key: 'h2h', label: 'Head-to-Head' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className="px-4 py-1.5 font-caption text-xs font-medium transition-all"
                  style={{
                    backgroundColor: mainTab === tab.key ? 'var(--color-primary)' : 'transparent',
                    color: mainTab === tab.key ? '#fff' : 'var(--color-muted-foreground)',
                  }}
                  onClick={() => setMainTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {mainTab === 'standings' && (
              <div className="overflow-x-auto">
                <DateFilter value={dateRange} onChange={setDateRange} />
              </div>
            )}
          </div>

          {gamesLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
            </div>
          ) : mainTab === 'h2h' ? (
            <HeadToHead players={players} games={games} gameStats={gameStats} />
          ) : (
            <>
              {/* Player Standings Table */}
              <div className="rounded-xl border overflow-hidden mb-8" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <h2 className="font-heading font-semibold text-base" style={{ color: 'var(--color-foreground)' }}>
                    <Icon name="Trophy" size={16} color="var(--color-primary)" className="inline mr-2" style={{ verticalAlign: '-2px' }} />
                    Player Standings
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--color-muted)' }}>
                        {SORT_COLUMNS.map((col) => (
                          <th
                            key={col.key}
                            className="px-3 py-2.5 text-left font-caption text-xs font-semibold cursor-pointer select-none hover:opacity-80 transition-opacity"
                            style={{ color: sortKey === col.key ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}
                            onClick={() => handleSort(col.key)}
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              {sortKey === col.key && (
                                <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={12} color="var(--color-primary)" />
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {playerRows?.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center">
                            <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>No games recorded yet.</p>
                            <Link to="/game-recording" className="inline-flex items-center gap-1 mt-2 font-caption text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                              Record a Game <Icon name="ArrowRight" size={12} color="currentColor" />
                            </Link>
                          </td>
                        </tr>
                      ) : (
                        playerRows?.map((row, i) => (
                          <tr key={row.id} style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : undefined }}>
                            <td className="px-3 py-2.5">
                              <Link
                                to={`/players/${row.id}`}
                                className="font-body text-sm font-medium hover:underline"
                                style={{ color: 'var(--color-primary)' }}
                              >
                                {row.name}
                              </Link>
                              {row.position && <span className="ml-1.5 font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>({row.position})</span>}
                            </td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>{row.wins}</td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-error, #dc2626)' }}>{row.losses}</td>
                            <td className="px-3 py-2.5">
                              <span className="font-data text-sm font-semibold" style={{ color: row.winPct >= 50 ? 'var(--color-success, #16a34a)' : 'var(--color-foreground)' }}>
                                {row.gamesPlayed > 0 ? `${row.winPct}%` : '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{row.avgPoints}</td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{row.avgRebounds}</td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{row.avgAssists}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {playerRows?.length > 0 && (
                  <div className="px-4 py-2" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-muted)' }}>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      {playerRows?.length} player{playerRows?.length !== 1 ? 's' : ''} &middot; Sorted by {SORT_COLUMNS.find((c) => c.key === sortKey)?.label} {sortDir === 'desc' ? '(high to low)' : '(low to high)'}
                    </p>
                  </div>
                )}
              </div>

              {/* Duo & Trio Win Rates */}
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <h2 className="font-heading font-semibold text-base" style={{ color: 'var(--color-foreground)' }}>
                    <Icon name="Users" size={16} color="var(--color-primary)" className="inline mr-2" style={{ verticalAlign: '-2px' }} />
                    When They Play Together
                  </h2>
                  <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
                    {['duos', 'trios'].map((tab) => (
                      <button
                        key={tab}
                        className="px-3 py-1.5 font-caption text-xs font-medium transition-all"
                        style={{
                          backgroundColor: comboTab === tab ? 'var(--color-primary)' : 'transparent',
                          color: comboTab === tab ? '#fff' : 'var(--color-muted-foreground)',
                        }}
                        onClick={() => setComboTab(tab)}
                      >
                        {tab === 'duos' ? 'Duos' : 'Trios'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--color-muted)' }}>
                        <th className="px-3 py-2.5 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Players</th>
                        <th className="px-3 py-2.5 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Games</th>
                        <th className="px-3 py-2.5 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Wins</th>
                        <th className="px-3 py-2.5 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Win%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!combos?.length ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                            {comboTab === 'duos' ? 'Need at least 2 games with the same duo to show stats.' : 'Need at least 2 games with the same trio to show stats.'}
                          </td>
                        </tr>
                      ) : (
                        combos?.map((combo, i) => (
                          <tr key={combo.playerIds.join('-')} style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : undefined }}>
                            <td className="px-3 py-2.5 font-body text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                              {combo.playerNames.join(' + ')}
                            </td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{combo.games}</td>
                            <td className="px-3 py-2.5 font-data text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>{combo.wins}</td>
                            <td className="px-3 py-2.5">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full font-data text-xs font-semibold"
                                style={{
                                  backgroundColor: combo.winPct >= 60 ? 'rgba(22,163,74,0.12)' : combo.winPct >= 40 ? 'rgba(234,179,8,0.12)' : 'rgba(220,38,38,0.08)',
                                  color: combo.winPct >= 60 ? '#16a34a' : combo.winPct >= 40 ? '#ca8a04' : '#dc2626',
                                }}
                              >
                                {combo.winPct}%
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default StatsPage;
